import { useRef, useState, type ReactNode } from "react";
import { errorMessage } from "../lib/format";
import { useUploads } from "../lib/useUploads";
import { UploadList } from "./Attachments";
import { Icon } from "./Icon";
import { MarkdownEditor } from "./MarkdownEditor";

export interface ComposerAction {
  label: string;
  variant?: "primary" | "danger" | "default";
  /** Allow submitting with an empty body (e.g. Approve). */
  allowEmpty?: boolean;
  onSubmit: (body: string, attachmentIds: string[]) => Promise<unknown>;
}

/**
 * Shared composer for comments, review decisions and answers to agent
 * questions: markdown + preview, file button, drag-and-drop and paste
 * upload via POST /api/attachments.
 */
/** Thrown by an action's onSubmit when the user backs out (e.g. a confirm dialog). */
export class SubmitCancelled extends Error {}

export function Composer({
  actions,
  placeholder = "Leave a comment…",
  rows = 4,
  children,
  hint,
  autoFocus,
}: {
  actions: ComposerAction[];
  placeholder?: string;
  rows?: number;
  /** Extra controls rendered left of the action buttons. */
  children?: ReactNode;
  hint?: ReactNode;
  autoFocus?: boolean;
}) {
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const uploads = useUploads();
  const fileInput = useRef<HTMLInputElement>(null);

  const run = async (a: ComposerAction) => {
    // (An action that throws SubmitCancelled keeps the draft and shows no error.)
    if (submitting || uploads.busy) return;
    if (!a.allowEmpty && !body.trim() && uploads.ids.length === 0) return;
    setSubmitting(a.label);
    setError(null);
    try {
      await a.onSubmit(body, uploads.ids);
      setBody("");
      uploads.clear();
    } catch (e) {
      if (!(e instanceof SubmitCancelled)) setError(errorMessage(e));
    } finally {
      setSubmitting(null);
    }
  };

  const empty = !body.trim() && uploads.ids.length === 0;

  return (
    <div className="composer">
      <MarkdownEditor
        value={body}
        onChange={setBody}
        placeholder={placeholder}
        rows={rows}
        onFiles={uploads.addFiles}
        onSubmitShortcut={() => actions[0] && run(actions[0])}
        autoFocus={autoFocus}
        footer={<UploadList items={uploads.items} onRemove={uploads.remove} />}
      />
      {error && <div className="error-box">{error}</div>}
      <div className="composer-bar">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => fileInput.current?.click()}
          title="Attach files (or drag and drop, or paste images)"
        >
          <Icon name="paperclip" size={14} /> Attach
        </button>
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) uploads.addFiles(e.target.files);
            e.target.value = "";
          }}
        />
        {hint && <span className="composer-hint muted">{hint}</span>}
        <span className="spacer" />
        {children}
        {actions.map((a) => (
          <button
            key={a.label}
            type="button"
            className={`btn btn-sm ${a.variant === "primary" ? "btn-primary" : a.variant === "danger" ? "btn-danger" : ""}`}
            disabled={!!submitting || uploads.busy || (!a.allowEmpty && empty)}
            onClick={() => run(a)}
          >
            {submitting === a.label ? "…" : a.label}
          </button>
        ))}
      </div>
    </div>
  );
}
